import React, { useEffect, useRef, useState } from "react";
import type { EquipmentCategoryType } from "../../../utils/types";
import useEquipmentContentSlider from "../../../hooks/useEquipmentContentSlider";
import * as Text from "../../../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import * as Button from "../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as TabStyles from "../styles/Tabs";
import * as SliderStyles from "../styles/Slider";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import ContentSlider from "./ContentSlider";
import { useWindowDimensions } from "../../../../../../common-deprecated/hooks";
import useSlider from "../../../../../../common-deprecated/hooks/useSlider";
import ItemList from "./ItemList";
import { useEquipmentV2Selector } from "../../../redux/store";
import { EquipmentV2Label } from "../../../utils/labels";
import { isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import SrOnly from "../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { trackToggleExpandedEquipment } from "../../../utils/tracking";

type EquipmentContentProps = {
    content: EquipmentCategoryType[];
};

const INITIAL_NUMBER_OF_ITEMS = 4;

const EquipmentContent = (props: EquipmentContentProps): JSX.Element => {
    const { content } = props;

    const commonSettings = useEquipmentV2Selector((state) => state.commonSettings);

    const { activeCategory, filteredItems, switchEquipment } = useEquipmentContentSlider(content);

    const viewPortRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const windowWidth = useWindowDimensions().width;
    const [sliderWidth, setSliderWidth] = useState<number>(0);
    const [viewportWidth, setViewportWidth] = useState<number>(0);
    const [showAllItems, setShowAllItems] = useState(false);

    // calculate widths on resize
    useEffect(() => {
        if (viewPortRef.current && sliderRef.current) {
            const { width } = viewPortRef.current.getBoundingClientRect();
            setViewportWidth(width);
            setSliderWidth(sliderRef.current.getBoundingClientRect().width);
        }
    }, [content.length, viewportWidth, windowWidth]);

    const sliderProps = useSlider(sliderWidth, viewportWidth);
    const { startTouchDrag, startMouseDrag, sliderX, enableDrag, enableCssTransition } = sliderProps;
    const { leftArrowClickPage, rightArrowClickPage, leftArrowEnabled, rightArrowEnabled } = sliderProps;

    const tabRefs = useRef<HTMLButtonElement[]>([]);
    const defaultIndex = 0;
    const [selectedIndex, setSelectedIndex] = useState(defaultIndex);

    const handleClick = (index: number): void => {
        setSelectedIndex(index);
    };

    const setIndex = (index: number): void => {
        const tab = tabRefs.current[index];
        if (tab) {
            // focus() will call the state setter
            // to display the associated tabpanel
            tab.focus();
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
        switch (event.key) {
            case "ArrowLeft":
                event.preventDefault();
                setIndex(selectedIndex - 1);
                break;
            case "ArrowRight":
                event.preventDefault();
                setIndex(selectedIndex + 1);
                break;
            case "Home":
                event.preventDefault();
                setIndex(0);
                break;
            case "End":
                event.preventDefault();
                setIndex(tabRefs.current.length - 1);
                break;
            default:
                break;
        }
    };

    // Show more items logic
    // sort the items based if they have assets or not. the items with assets shouls always come first
    let items = filteredItems?.sort((a, b) => {
        if (b.assets?.length) return 1;
        else return -1;
    });
    const hasMoreItems = items && INITIAL_NUMBER_OF_ITEMS < items?.length;
    if (!showAllItems && hasMoreItems && items) {
        items = items.splice(0, INITIAL_NUMBER_OF_ITEMS);
    }
    return (
        <>
            <TabStyles.Overflower>
                {/* LeftButton should only be visible when TabStyles.Wrapper is bigger than TabStyles.Overflow and when a tabjump to left is possible */}
                <TabStyles.LeftButton onClick={leftArrowClickPage} visible={leftArrowEnabled}>
                    <SrOnly>
                        <EquipmentV2Label
                            label={
                                isRTL(commonSettings) ? "equipmentA11yButtonTabPrevious" : "equipmentA11yButtonTabNext"
                            }
                        />
                    </SrOnly>
                    <Icon variant={isRTL(commonSettings) ? "chevronRight" : "chevronLeft"} />
                </TabStyles.LeftButton>
                <TabStyles.Wrapper ref={viewPortRef}>
                    <SliderStyles.Track
                        ref={sliderRef}
                        left={enableDrag ? sliderX : 0}
                        position="absolute"
                        onTouchStart={enableDrag ? startTouchDrag : undefined}
                        onMouseDown={enableDrag ? startMouseDrag : undefined}
                        enableTransition={enableCssTransition}
                        role="tablist"
                        aria-orientation="horizontal"
                    >
                        {content.map((categories, index) => (
                            <TabStyles.Tab
                                key={categories.code}
                                isActive={categories.code === activeCategory?.code}
                                ref={(elem) => {
                                    tabRefs.current[index] = elem as HTMLButtonElement;
                                }}
                                onClick={() => {
                                    setShowAllItems(false);
                                    switchEquipment(categories.code);
                                    handleClick(index);
                                }}
                                onKeyDown={onKeyDown}
                                onFocus={() => {
                                    handleClick(index);
                                }}
                                aria-controls={`${commonSettings.component}-tab-${categories.id}`}
                                aria-selected={categories.code === activeCategory?.code}
                                id={`${commonSettings.component}-tab-${categories.id}-button`}
                                role="tab"
                                tabIndex={selectedIndex === index ? 0 : -1}
                            >
                                {categories.title}
                            </TabStyles.Tab>
                        ))}
                    </SliderStyles.Track>
                </TabStyles.Wrapper>
                <TabStyles.RightButton onClick={rightArrowClickPage} visible={rightArrowEnabled}>
                    <Icon variant={isRTL(commonSettings) ? "chevronLeft" : "chevronRight"} />
                    <SrOnly>
                        <EquipmentV2Label
                            label={
                                isRTL(commonSettings) ? "equipmentA11yButtonTabNext" : "equipmentA11yButtonTabPrevious"
                            }
                        />
                    </SrOnly>
                </TabStyles.RightButton>
            </TabStyles.Overflower>
            {activeCategory && items && (
                <div
                    aria-labelledby={`${commonSettings.component}-tab-${activeCategory.id}-button`}
                    id={`${commonSettings.component}-tab-${activeCategory.id}`}
                    role="tabpanel"
                    tabIndex={0}
                >
                    <ContentSlider
                        items={items.filter((item) => item.assets?.length)}
                        categoryId={activeCategory.id}
                        categoryName={activeCategory.title}
                    />
                    <ItemList
                        items={items.filter((item) => !item.assets?.length)}
                        categoryId={activeCategory.id}
                        categoryName={activeCategory.title}
                    />
                    {!showAllItems && hasMoreItems && (
                        <Button.Secondary
                            onClick={() => {
                                setShowAllItems(true);
                                trackToggleExpandedEquipment(commonSettings.component, activeCategory.title);
                            }}
                            m="16px auto 16px 0"
                        >
                            <Text.Button>
                                <EquipmentV2Label label="equipmentViewMore" />
                            </Text.Button>
                        </Button.Secondary>
                    )}

                    {showAllItems && hasMoreItems && (
                        <Button.Secondary
                            onClick={() => {
                                setShowAllItems(false);
                                trackToggleExpandedEquipment(commonSettings.component, activeCategory.title, false);
                            }}
                            m="16px auto 16px 0"
                        >
                            <Text.Button>
                                <EquipmentV2Label label="equipmentViewLess" />
                            </Text.Button>
                        </Button.Secondary>
                    )}
                </div>
            )}
        </>
    );
};
export default EquipmentContent;
